import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum GraceFarmsPackingSlipLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
}

export const graceFarmsPackingSlipAddressSchema = yup.object().shape({
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})
export type GraceFarmsPackingSlilpAddress = yup.InferType<typeof graceFarmsPackingSlipAddressSchema>

export const graceFarmsPackingSlipFileDataSchema = yup.object().shape({
  billing: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: graceFarmsPackingSlipAddressSchema.nullable().optional(),
    contactName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  graceFarmsPackingSlipNumber: yup.string().nullable().optional(),
  customerPoNumber: yup.string().nullable().optional(),
  date: yup.string().nullable().optional(),
  trackingNumber: yup.string().nullable().optional(),
  shipFromLocation: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: graceFarmsPackingSlipAddressSchema.nullable().optional(),
    contactName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  shipping: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: graceFarmsPackingSlipAddressSchema.nullable().optional(),
    contactName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        [GraceFarmsPackingSlipLineItemKeys.itemNumber]: yup.number().nullable().optional(),
        [GraceFarmsPackingSlipLineItemKeys.itemName]: yup.string().nullable().optional(),
        [GraceFarmsPackingSlipLineItemKeys.description]: yup.string().nullable().optional(),
        [GraceFarmsPackingSlipLineItemKeys.itemQty]: yup.string().nullable().optional(),
      }),
    )
    .required(),
  notes: yup.string().nullable().optional(),
})

export type GraceFarmsPackingSlipFileData = yup.InferType<
  typeof graceFarmsPackingSlipFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<GraceFarmsPackingSlipFileData>

export const graceFarmsPackingSlipFileMappings: RequiredFileData = {
  billing: {
    companyName: '4b375888-2f5b-4479-8567-797debb65566',
    address: {
      addressLine1: 'ad829f38-d1fc-4875-a305-143ce5eb8826',
      addressLine2: '3b1f4941-7736-4669-b325-805dcb46f689',
      postalCode: '295d965f-4894-4ff1-a5f3-49217127e32a',
      city: '18eb582d-e07b-4ee0-adeb-54e51142df47',
      state: '66115243-b12d-492c-ac02-a1e8e3dda8c4',
      country: 'acc85283-6713-4b10-8789-961d99c9fc1e',
    },
    contactName: '3cb9eeb5-b749-4569-bd11-12f792ec10b0',
    phoneNumber: 'd0f00f1f-43f6-43b5-ab32-578c988e6130',
    emailAddress: '8c29be40-dbb1-44eb-bee8-789cc8417833',
  },
  graceFarmsPackingSlipNumber: '81d22ac0-e15c-4069-bb41-53fec3a3b5e7',
  customerPoNumber: '6c0b6ebe-a754-4c47-94cd-272910b750ff',
  date: '83582745-bda5-4ec5-837f-6bb6d22c0b83',
  trackingNumber: 'd929c5d2-0de9-4e45-a50d-7adba6c9b2d7',
  shipFromLocation: {
    companyName: 'b40fb963-4c4e-4739-8f2e-269391e82093',
    address: {
      addressLine1: '5e336496-2499-4ca2-81bc-6a3857b2e141',
      addressLine2: '5fc62a81-895a-4abe-93d5-8eefbcd1d56c',
      postalCode: '90411050-2313-4a0b-a018-125b4051b261',
      city: 'a72d82d1-0b4a-4128-8d4d-0e18cd13cc1f',
      state: 'aff40d48-8a57-4b9b-9a50-503c9276a0b2',
      country: 'ff9905b9-8723-4445-a38e-7772cba13fa3',
    },
    contactName: 'c14b3571-e82a-412e-aa1b-7ccd2949e582',
    phoneNumber: '69803380-03ed-4f4c-aa72-a27fe7c18770',
    emailAddress: '3a244f34-5d28-4326-9ad5-f5d64283d7fd',
  },
  shipping: {
    companyName: '43790eb4-3ee4-4bc2-b527-b1b38c80037f',
    address: {
      addressLine1: 'd7116235-d923-421b-bb7f-bef44910f98c',
      addressLine2: '5f00adad-a998-4ee3-8cc5-be6f58dbcccf',
      postalCode: 'b5ffbef8-47f0-4b3e-9d00-1cdb8da8cb9a',
      city: 'aca98bfa-9978-4281-80a3-3f8db9defbe7',
      state: '95d60976-ec3c-48e8-9374-80e1753edb94',
      country: 'fea7274b-685d-4016-b5f3-1dca05a81b60',
    },
    contactName: '1120cd8d-ac7f-4f73-b5d2-186e37c789a5',
    phoneNumber: 'd59be99c-1b63-47a0-82fa-33032f4a1ca3',
    emailAddress: '6bbebab4-e5ea-4b0d-a3f8-404b1184d23f',
  },
  items: [
    {
      itemNumber: null,
      itemName: '370b8355-3f78-4fa2-918c-eece487fa014',
      description: 'bb1d5c1d-7c41-43fb-bc80-51d3deb6da45',
      itemQty: '2d99518c-c714-4733-be1a-ef3b99cfb113',
    },
  ],
  notes: '975d80c5-3c42-46f6-956d-f06001117d2c',
}
